import React, { useState, useEffect, useRef, useCallback } from "react";
import Select from "react-select";
import moment from "moment";
import { DB_DATE_FORMAT, DATERANGE_LIST } from "../../constants";

const DateRangeDropdown = (props) => {
  const {
    onDateRangeSelect,
    selectedDate,
    startDate: propStartDate,
    endDate: propEndDate,
    selectedRange,
    statCardText,
    className = "",
    options = DATERANGE_LIST,
    style = {},
    allowCustomRange = true,
    showDateInputs: propShowDateInputs,
    onStartDateChange,
    onEndDateChange,
    onDateInputChange,
    onApply,
    onSearchDate,
  } = props;

  const containerRef = useRef(null);
  const popupRef = useRef(null);

  const initialStartDate =
    propStartDate || selectedDate || moment().format(DB_DATE_FORMAT);
  const initialEndDate =
    propEndDate ||
    propStartDate ||
    selectedDate ||
    moment().format(DB_DATE_FORMAT);

  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [showPopup, setShowPopup] = useState(Boolean(propShowDateInputs));
  const [isCustomActive, setIsCustomActive] = useState(
    selectedRange === "between" ||
      statCardText === "Custom Range" ||
      statCardText === "Date Range" ||
      Boolean(propShowDateInputs),
  );

  useEffect(() => {
    if (propStartDate) {
      setStartDate(propStartDate);
    } else if (
      selectedDate &&
      (selectedRange === "between" ||
        statCardText === "Custom Range" ||
        statCardText === "Date Range")
    ) {
      setStartDate(selectedDate);
    }
  }, [propStartDate, selectedDate, selectedRange, statCardText]);

  useEffect(() => {
    if (propEndDate) {
      setEndDate(propEndDate);
    } else if (
      selectedDate &&
      (selectedRange === "between" ||
        statCardText === "Custom Range" ||
        statCardText === "Date Range")
    ) {
      setEndDate(selectedDate);
    }
  }, [propEndDate, selectedDate, selectedRange, statCardText]);

  useEffect(() => {
    if (
      selectedRange === "between" ||
      statCardText === "Custom Range" ||
      statCardText === "Date Range"
    ) {
      setIsCustomActive(true);
    } else if (
      (selectedRange && selectedRange !== "between") ||
      (statCardText &&
        statCardText !== "Custom Range" &&
        statCardText !== "Date Range")
    ) {
      setIsCustomActive(false);
      setShowPopup(false);
    }
  }, [selectedRange, statCardText]);

  const handleCancel = useCallback(() => {
    setShowPopup(false);
    const revertedStartDate =
      propStartDate || selectedDate || moment().format(DB_DATE_FORMAT);
    const revertedEndDate =
      propEndDate ||
      propStartDate ||
      selectedDate ||
      moment().format(DB_DATE_FORMAT);

    setStartDate(revertedStartDate);
    setEndDate(revertedEndDate);

    if (onStartDateChange) {
      onStartDateChange(revertedStartDate);
    }
    if (onEndDateChange) {
      onEndDateChange(revertedEndDate);
    }

    if (
      selectedRange !== "between" &&
      statCardText !== "Custom Range" &&
      statCardText !== "Date Range"
    ) {
      setIsCustomActive(false);
    }
  }, [
    propStartDate,
    propEndDate,
    selectedDate,
    selectedRange,
    statCardText,
    onStartDateChange,
    onEndDateChange,
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target) &&
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        handleCancel();
      }
    };

    if (showPopup) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPopup, handleCancel]);

  const filteredOptions = options.filter(
    (item) =>
      item.name !== "Date Range" &&
      item.name !== "Custom Range" &&
      item.value !== "between",
  );

  const effectiveOptions = allowCustomRange
    ? [
        { date: null, name: "Custom Range", value: "between" },
        ...filteredOptions,
      ]
    : options;

  const getRangeFromName = (name) => {
    if (name.includes("Today") || name.includes("Yesterday")) return "day";
    if (name.includes("Week")) return "week";
    if (name.includes("Month")) return "month";
    if (name.includes("Year")) return "year";
    if (name.includes("All-time")) return "allTime";
    if (name.includes("Date Range") || name.includes("Custom Range"))
      return "between";
    return "day";
  };

  const selectOptions = effectiveOptions.map((item) => ({
    ...item,
    label: item.name,
    value: item.name, // Use name as unique value for react-select tracking
    rangeValue: item.value || getRangeFromName(item.name),
  }));

  // Map "statCardQuery" values (day, week, month, year, allTime, between) to labels if statCardText is not descriptive
  const getLabelFromRange = (range) => {
    if (range === "day") return "Today";
    if (range === "week") return "This Week";
    if (range === "month") return "This Month";
    if (range === "year") return "This Year";
    if (range === "allTime") return "All-time";
    if (range === "between") return "Custom Range";
    return null;
  };

  const selectedOption =
    (isCustomActive
      ? selectOptions.find((o) => o.rangeValue === "between")
      : null) ||
    selectOptions.find((o) => o.label === statCardText) ||
    (statCardText === "Date Range" || statCardText === "Custom Range"
      ? selectOptions.find((o) => o.label === "Custom Range")
      : null) ||
    selectOptions.find((o) => o.label === getLabelFromRange(selectedRange)) ||
    (selectedDate && selectOptions.find((o) => o.date === selectedDate)) ||
    null;

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: "#14C49B",
      borderColor: "#14C49B",
      borderRadius: "0.42rem",
      minHeight: "38px",
      boxShadow: state.isFocused
        ? "0 0 0 0.2rem rgba(20, 196, 155, 0.25)"
        : "none",
      "&:hover": {
        borderColor: "#11a885",
        backgroundColor: "#11a885",
      },
      cursor: "pointer",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#FFFFFF",
      fontWeight: "500",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "rgba(255, 255, 255, 0.8)",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: "#FFFFFF",
      "&:hover": {
        color: "#FFFFFF",
      },
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "0.42rem",
      boxShadow: "0px 0px 50px 0px rgba(82, 63, 105, 0.15)",
      zIndex: 9999,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#14C49B"
        : state.isFocused
        ? "#e8f9f5"
        : "transparent",
      color: state.isSelected ? "#FFFFFF" : "#3F4254",
      fontSize: "0.9rem",
      cursor: "pointer",
      "&:active": {
        backgroundColor: "#14C49B",
      },
    }),
  };

  const handleChange = (option) => {
    const isCustom =
      option.rangeValue === "between" ||
      option.label === "Custom Range" ||
      option.label === "Date Range";

    if (isCustom) {
      setIsCustomActive(true);
      setShowPopup(true);
      // Do not refresh immediately upon clicking Custom Range!
      return;
    }

    setIsCustomActive(false);
    setShowPopup(false);

    const mockEvent = {
      currentTarget: { textContent: option.label },
      preventDefault: () => {},
    };

    if (onDateRangeSelect) {
      onDateRangeSelect(mockEvent, option.date, option.name, option.rangeValue);
    }
  };

  const handleStartDateChange = (e) => {
    const val = e.target.value;
    setStartDate(val);
    if (onStartDateChange) {
      onStartDateChange(val);
    }
    if (onDateInputChange) {
      onDateInputChange(e, "startDate");
    }
    if (val && endDate && moment(val).isAfter(moment(endDate))) {
      setEndDate(val);
      if (onEndDateChange) {
        onEndDateChange(val);
      }
    }
  };

  const handleEndDateChange = (e) => {
    const val = e.target.value;
    setEndDate(val);
    if (onEndDateChange) {
      onEndDateChange(val);
    }
    if (onDateInputChange) {
      onDateInputChange(e, "endDate");
    }
  };

  const isRangeValid =
    Boolean(startDate && endDate) &&
    moment(startDate).isValid() &&
    moment(endDate).isValid() &&
    moment(startDate).isSameOrBefore(moment(endDate));

  const handleApply = (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!isRangeValid) return;

    setShowPopup(false);
    setIsCustomActive(true);

    const mockEvent = {
      currentTarget: { textContent: "Custom Range" },
      preventDefault: () => {},
      startDate,
      endDate,
    };

    if (onDateRangeSelect) {
      onDateRangeSelect(
        mockEvent,
        startDate,
        "Custom Range",
        "between",
        endDate,
      );
    }
    if (onSearchDate) {
      onSearchDate(startDate, endDate);
    }
    if (onApply) {
      onApply({ startDate, endDate, dateRange: "between" });
    }
  };

  return (
    <div
      ref={containerRef}
      className={`position-relative d-inline-flex align-items-center ${className}`}
      style={style}
    >
      <div style={{ minWidth: "150px" }}>
        <Select
          options={selectOptions}
          value={selectedOption}
          onChange={handleChange}
          styles={customStyles}
          isSearchable={false}
          placeholder="Select Range..."
        />
      </div>

      {isCustomActive && (
        <button
          type="button"
          className="btn btn-sm btn-icon ml-2 d-flex align-items-center justify-content-center"
          onClick={() => setShowPopup((prev) => !prev)}
          title="Edit custom date range"
          style={{
            height: "38px",
            width: "38px",
            borderRadius: "0.42rem",
            backgroundColor: showPopup ? "#e8f9f5" : "#FFFFFF",
            borderColor: "#14C49B",
            borderWidth: "1px",
            borderStyle: "solid",
            color: "#14C49B",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <i
            className="fa fa-calendar-alt p-0"
            style={{ color: "#14C49B", fontSize: "0.95rem" }}
          />
        </button>
      )}

      {showPopup && (
        <div
          ref={popupRef}
          className="position-absolute bg-white rounded shadow-lg p-3 border"
          style={{
            top: "calc(100% + 6px)",
            right: 0,
            zIndex: 10000,
            minWidth: "290px",
            borderColor: "#E4E6EF",
            boxShadow: "0px 10px 30px 0px rgba(82, 63, 105, 0.15)",
          }}
        >
          <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
            <span
              className="font-weight-bolder text-dark"
              style={{ fontSize: "0.9rem" }}
            >
              Custom Date Range
            </span>
            <button
              type="button"
              className="btn btn-xs btn-icon btn-clean btn-hover-light"
              onClick={handleCancel}
              style={{
                width: "22px",
                height: "22px",
                border: "none",
                background: "transparent",
                cursor: "pointer",
              }}
              title="Close"
            >
              <i
                className="fa fa-times text-muted"
                style={{ fontSize: "0.75rem" }}
              />
            </button>
          </div>

          <div className="form-group mb-2">
            <label
              className="text-muted font-weight-bold mb-1"
              style={{ fontSize: "0.75rem", textTransform: "uppercase" }}
            >
              Start Date
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={startDate || ""}
              onChange={handleStartDateChange}
              max={endDate || undefined}
              style={{ fontSize: "0.85rem", color: "#3F4254" }}
            />
          </div>

          <div className="form-group mb-3">
            <label
              className="text-muted font-weight-bold mb-1"
              style={{ fontSize: "0.75rem", textTransform: "uppercase" }}
            >
              End Date
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={endDate || ""}
              min={startDate || undefined}
              onChange={handleEndDateChange}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleApply(e);
              }}
              style={{ fontSize: "0.85rem", color: "#3F4254" }}
            />
          </div>

          {startDate &&
            endDate &&
            moment(startDate).isAfter(moment(endDate)) && (
              <div
                className="text-danger mb-2"
                style={{ fontSize: "0.75rem", lineHeight: "1.2" }}
              >
                Start date cannot be after end date.
              </div>
            )}

          <div className="d-flex justify-content-end align-items-center pt-2 border-top">
            <button
              type="button"
              className="btn btn-sm btn-light mr-2"
              onClick={handleCancel}
              style={{
                fontSize: "0.8rem",
                fontWeight: "600",
                borderRadius: "0.35rem",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-sm text-white px-3"
              onClick={handleApply}
              disabled={!isRangeValid}
              style={{
                backgroundColor: isRangeValid ? "#14C49B" : "#A1A5B7",
                borderColor: isRangeValid ? "#14C49B" : "#A1A5B7",
                fontSize: "0.8rem",
                fontWeight: "600",
                borderRadius: "0.35rem",
                cursor: isRangeValid ? "pointer" : "not-allowed",
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeDropdown;
